import { HttpResponse } from '@angular/common/http';
import { FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { EMPTY, merge, Observable, ReplaySubject, Subject, switchMap, takeUntil, catchError } from 'rxjs';
import { APICallStatus } from '../models/api-call-status.mode';
import { parsePaginationHeaders, PaginationMeta } from './pagination-helpers';
import { navigationSegments, toSortExpression } from './list-grid-helpers';

export abstract class KeyedListGridBase<T extends { repairCode: string; repairMode: string }, F> implements PaginationMeta {
  data: T[] = [];
  mockDataForLoadingAnimation: { id: number }[] = Array.from({ length: 30 }, (_, index) => ({ id: index }));
  firstPage?: number;
  previousPage?: number;
  currentPage = 1;
  nextPage?: number;
  lastPage?: number;
  totalItemCount?: number;
  pageLimit = 20;
  sort: string | undefined;
  filterParam: F;
  apiCallStatus: APICallStatus = 'NOT_STARTED';

  protected readonly destroyed$ = new ReplaySubject<boolean>(1);
  private readonly loadTrigger$ = new Subject<boolean>();

  protected constructor(
    protected readonly router: Router,
    initialFilter: F,
  ) {
    this.filterParam = initialFilter;
  }

  protected abstract loadPage(page: number, limit: number, sort: string | undefined, filters: F): Observable<HttpResponse<T[]>>;
  protected abstract applyFilters(): void;
  protected abstract get basePath(): string;

  protected initializeList(): void {
    this.loadTrigger$
      .pipe(
        switchMap(() =>
          this.loadPage(this.currentPage, this.pageLimit, this.sort, this.filterParam).pipe(
            catchError(() => {
              this.apiCallStatus = 'FAILED';
              this.data = [];
              return EMPTY;
            }),
          ),
        ),
        takeUntil(this.destroyed$),
      )
      .subscribe((response) => {
        this.data = response.body || [];
        if (response.headers) {
          Object.assign(this, parsePaginationHeaders(response.headers, this.currentPage));
        }
        this.apiCallStatus = 'COMPLETED_SUCCESFULLY';
      });
    this.reload();
  }

  protected subscribeToFilterTriggers(filterForm: FormGroup, searchForm: FormGroup): void {
    merge(filterForm.get('applyFilter')!.valueChanges, searchForm.get('appliedSearchTextFilter')!.valueChanges)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((value) => {
        if (value === true) {
          this.applyFilters();
        }
      });
  }

  onSortDirectionChange(event: Event): void {
    const sort = toSortExpression(event, 'id');
    if (!sort) return;

    this.sort = sort;
    this.currentPage = 1;
    this.reload();
  }

  onPageLimitChanged(newLimit: number): void {
    this.currentPage = 1;
    this.pageLimit = newLimit;
    this.reload();
  }

  onPageChanged(newPage: number): void {
    this.currentPage = newPage;
    this.reload();
  }

  onEdit(row: T): void {
    this.router.navigate(navigationSegments(this.basePath, row, 'edit'));
  }

  onView(row: T): void {
    this.router.navigate(navigationSegments(this.basePath, row));
  }

  reload(): void {
    this.apiCallStatus = 'IN_PROGRESS';
    this.loadTrigger$.next(true);
  }

  destroyList(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
